import React, { useMemo, useState, memo, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, BookOpen, Play, TrendingUp, TrendingDown, Minus, Target, CheckCircle, BarChart2, Lightbulb, ChevronDown, ChevronRight, Zap, RefreshCw, Sparkles, Eye } from 'lucide-react';
import { type ModuleData, getModuleIcon, getModuleDisplayInfo, getLearningGoal, getFamilyBySubSkill, getModuleHexColor, getModuleColors, getQuestionsForTier } from '../../content';
import { ModuleProgress } from '../../app/types';
import { Button } from '../ui/Button';
import { ProgressBar } from '../ui/Progress';
import { useMediaQuery } from 'react-responsive';
import { GroupId } from '../../config/systemConfig';
import { useAppStore } from '../../app/hooks/useAppStore';
import { computeTierProgress, computeSkillMasteries } from '../../mastery';
import type { TierMasteryFacts, TierMasteryProvider } from '../../mastery/tierProgress';
import { getFamilyMasteryKey } from '../../mastery/familyMasteryEngine';
import { getQuestionStateKey } from '../../mastery/questionAttemptHistory';
import { formatSkill } from '../../utils/formatters';
import { useFocusTrap } from '../../app/hooks/useFocusTrap';
import { useEscapeKey } from '../../app/hooks/useEscapeKey';
import { useBodyScrollLock } from '../../app/hooks/useBodyScrollLock';
import { useSwipeToDismiss } from '../../app/hooks/useSwipeToDismiss';
interface ModulePanelProps {
  isOpen: boolean;
  onClose: () => void;
  module: ModuleData | null;
  progress: ModuleProgress | null;
  onStartPractice: (moduleId: string) => void;
  onViewExplanation: (moduleId: string) => void;
  selectedGroup: GroupId;
}
export const ModulePanel = memo(function ModulePanel({
  isOpen,
  onClose,
  module,
  progress,
  onStartPractice,
  onViewExplanation,
  selectedGroup
}: ModulePanelProps) {
  const isMobile = useMediaQuery({
    maxWidth: 768
  });
  const focusTrapRef = useFocusTrap(isOpen);
  useEscapeKey(onClose, isOpen);
  useBodyScrollLock(isOpen);
  const swipe = useSwipeToDismiss({
    onDismiss: onClose,
    direction: isMobile ? 'bottom' : 'right',
    disabled: !isOpen
  });
  const {
    state,
    getStartingLevel,
    getModuleMasteryForGroup,
    getAnsweredQuestions
  } = useAppStore();
  const [expandedSkill, setExpandedSkill] = useState<string | null>(null);
  // Get dynamic content
  const displayInfo = module ? getModuleDisplayInfo(module.id, selectedGroup) : null;
  const title = displayInfo?.title || module?.title;
  const description = displayInfo?.description || module?.description;
  // v10.0: Use group-scoped mastery
  const masteryPercentage = module ? getModuleMasteryForGroup(module.id) : 0;
  // Compute skill-level aggregated masteries
  // FIX 1: Pass state.familyMastery to computeSkillMasteries
  const skillMasteries = useMemo(() => {
    if (!module) return [];
    return computeSkillMasteries(module.id, selectedGroup, state.questionStates, state.familyMastery);
  }, [module, selectedGroup, state.questionStates, state.familyMastery]);
  // FIX 2: Fallback for answered count (handles demo accounts with synthetic question IDs)
  const answeredCount = useMemo(() => {
    if (!module) return 0;
    const baseCount = getAnsweredQuestions(module.id).length;
    if (baseCount > 0) return baseCount;
    // Fallback: sum from skillMasteries (which now derives it from familyMastery)
    return skillMasteries.reduce((sum, skill) => sum + skill.questionsAnswered, 0);
  }, [module, getAnsweredQuestions, skillMasteries]);
  // ── FG2 — Build TierMasteryProvider from store state ───────────────────
  // ARCHITECTURAL_PEER_REVIEW_PLAN_2026 §FG2. Dependency-inversion: the
  // provider is INJECTED into computeTierProgress so the mastery domain
  // stays decoupled. Returns null per tier when no data → legacy count-path.
  const familyMasteryProvider = useMemo<TierMasteryProvider>(() => {
    return (tier: number): TierMasteryFacts | null => {
      if (!module) return null;
      const questionsAtTier = getQuestionsForTier(module.id, tier);
      if (questionsAtTier.length === 0) return null;
      // Aggregate per-tier mastery: max familyMastery across families
      // present at this tier. Max (not average) reflects "the learner
      // has DEMONSTRATED mastery somewhere in this tier" — the right
      // signal for the fast-path. Average would penalise breadth.
      const familiesAtTier = new Set<string>();
      questionsAtTier.forEach(q => {
        if (q.familyId) familiesAtTier.add(q.familyId);
      });
      let maxMastery = 0;
      let anyFamilySeen = false;
      familiesAtTier.forEach(familyId => {
        const key = getFamilyMasteryKey(familyId, selectedGroup);
        const fm = state.familyMastery[key];
        if (fm && fm.attempts > 0) {
          anyFamilySeen = true;
          if (fm.masteryScore > maxMastery) maxMastery = fm.masteryScore;
        }
      });
      if (!anyFamilySeen) return null;
      // Unique-questions-seen: distinct question states with attempts > 0.
      let uniqueQuestionsSeen = 0;
      // Touch-of-hardest: any answered question whose citoDifficulty
      // falls in the top range of the tier's actually-observed difficulties.
      const difficulties = questionsAtTier.map(q => (q as Record<string, unknown>).citoDifficulty).filter((d): d is number => typeof d === 'number' && Number.isFinite(d));
      const maxDifficulty = difficulties.length > 0 ? Math.max(...difficulties) : 0;
      const highDifficultyThreshold = maxDifficulty - 0.15;
      let touchedHighestDifficultyBand = false;
      // ── FG2 Phase 2 — Equivalence-class accounting ──────────────────────
      // ARCHITECTURAL_PEER_REVIEW_PLAN_2026 §FG2 fase 2. Group questions in
      // this tier by their `equivalenceClass` tag (questions without one
      // form a singleton class keyed by their id — preserves Phase-1
      // behaviour). Mastering ANY representative of a class counts the
      // whole class as "touched" for breadth/coverage. This collapses the
      // perceived path through long flat-distribution families like
      // REK-TAFELS-BASIS (100+ variants of essentially the same skill)
      // without lowering the actual mastery bar.
      const allClasses = new Set<string>();
      const seenClasses = new Set<string>();
      questionsAtTier.forEach(q => {
        const classKey = typeof (q as {
          equivalenceClass?: unknown;
        }).equivalenceClass === 'string' && ((q as {
          equivalenceClass?: string;
        }).equivalenceClass as string).length > 0 ? (q as {
          equivalenceClass: string;
        }).equivalenceClass as string :
        // Fallback: question forms its own singleton class. Prefix
        // with `__q:` to guarantee no collision with author-provided
        // class tags.
        `__q:${q.id}`;
        allClasses.add(classKey);
        const stateKey = getQuestionStateKey(q.id, selectedGroup);
        const qState = state.questionStates[stateKey];
        if (qState && qState.attempts > 0) {
          uniqueQuestionsSeen += 1;
          seenClasses.add(classKey);
          const d = (q as Record<string, unknown>).citoDifficulty;
          if (typeof d === 'number' && Number.isFinite(d) && d >= highDifficultyThreshold) {
            touchedHighestDifficultyBand = true;
          }
        }
      });
      return {
        masteryScore: maxMastery,
        uniqueQuestionsSeen,
        touchedHighestDifficultyBand,
        totalEquivalenceClasses: allClasses.size,
        uniqueEquivalenceClassesSeen: seenClasses.size
      };
    };
  }, [module, selectedGroup, state.familyMastery, state.questionStates]);
  if (!module || !progress) return null;
  const IconComponent = getModuleIcon(module.icon);
  const bgColorMap: Record<string, string> = {
    'text-blue-500': 'bg-blue-50 text-blue-600 border-blue-100',
    'text-indigo-500': 'bg-indigo-50 text-indigo-600 border-indigo-100',
    'text-emerald-500': 'bg-emerald-50 text-emerald-600 border-emerald-100',
    'text-rose-500': 'bg-rose-50 text-rose-600 border-rose-100',
    'text-amber-500': 'bg-amber-50 text-amber-600 border-amber-100',
    'text-purple-500': 'bg-purple-50 text-purple-600 border-purple-100',
    'text-teal-500': 'bg-teal-50 text-teal-600 border-teal-100',
    'text-cyan-500': 'bg-cyan-50 text-cyan-600 border-cyan-100',
    'text-orange-500': 'bg-orange-50 text-orange-600 border-orange-100',
    'text-sky-500': 'bg-sky-50 text-sky-600 border-sky-100',
    'text-yellow-500': 'bg-yellow-50 text-yellow-600 border-yellow-100',
    'text-fuchsia-500': 'bg-fuchsia-50 text-fuchsia-600 border-fuchsia-100'
  };
  const themeClasses = bgColorMap[module.color] || 'bg-slate-50 text-slate-600 border-slate-100';
  const moduleHex = getModuleHexColor(module.color);
  const moduleColorSet = getModuleColors(module.color);
  // Compute Tier Progress for "Current Level" display
  const startingTier = getStartingLevel(module.id, selectedGroup) || 1;
  const tierProgressInfo = computeTierProgress(module.id, selectedGroup, state.questionStates, startingTier, familyMasteryProvider);
  const currentTier = tierProgressInfo.tierDetails.find(t => t.isCurrent) || tierProgressInfo.tierDetails[0];
  // FIX 3: Use title instead of label for user-friendly display (e.g., "Midden Groep 7" instead of "M7")
  const tierDisplay = currentTier?.title || currentTier?.label || '-';
  // FG2 — Subtle UI signal for the current tier's unlock path.
  // Surfaces ONLY when meaningful: a mastery-path completion (the fast
  // learner is being respected) or a coverage-gap hint (high mastery,
  // some variants still unseen — invitation, never blocker).
  const tierUnlockedViaMastery = currentTier?.via === 'mastery';
  const coverageGapHint = currentTier?.coverageGapWithHighMastery === true;
  const unseenVariantCount = currentTier && typeof currentTier.coverage === 'number' ? Math.max(0, Math.round((1 - currentTier.coverage) * currentTier.totalQuestions)) : 0;
  // Sort skills: Struggling first, then by lowest mastery
  const sortedSkills = [...skillMasteries].sort((a, b) => {
    if (a.trend === 'struggling' && b.trend !== 'struggling') return -1;
    if (b.trend === 'struggling' && a.trend !== 'struggling') return 1;
    return a.masteryScore - b.masteryScore;
  });
  // Find struggling skill for Smart Tip
  const strugglingSkill = sortedSkills.find(s => s.trend === 'struggling' || s.masteryScore < 35);
  // Find decaying skills for Review Tip
  const decayingSkills = sortedSkills.filter(s => s.hasDecay);
  return <AnimatePresence data-id="element-1643">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40" data-id="element-1644" />

          {/* Panel */}
          <motion.div ref={focusTrapRef} role="dialog" aria-modal="true" aria-labelledby="module-panel-title" initial={isMobile ? {
        y: '100%'
      } : {
        x: '100%'
      }} animate={isMobile ? {
        y: 0
      } : {
        x: 0
      }} exit={isMobile ? {
        y: '100%'
      } : {
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} {...swipe.dragProps} className={`
              fixed z-50 bg-[#FAFAF8] shadow-2xl flex flex-col overflow-hidden
              ${isMobile ? 'inset-x-0 bottom-0 h-[92vh] rounded-t-[2rem]' : 'inset-y-0 right-0 w-full max-w-md border-l border-white/50'}
            `} data-id="element-1645">
            {/* Colored Accent Line */}
            <div className="h-1.5 w-full shrink-0" style={{
          backgroundColor: moduleHex
        }} data-id="element-1646" />

            {/* Mobile Drag Handle — only this region initiates swipe-to-dismiss
                so the inner scrollable content keeps scrolling on touch. */}
            {isMobile && <div {...swipe.handleProps} onClick={onClose} className="w-full flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing" role="button" aria-label="Sleep omlaag of tik om te sluiten" data-id="element-1647">
                <div className="w-12 h-1.5 bg-slate-300/50 rounded-full" data-id="element-1648" />
              </div>}

            {/* Compact Header */}
            <div className="px-6 py-4 flex items-start justify-between bg-white/80 backdrop-blur-md border-b border-slate-200/50 sticky top-0 z-10" data-id="element-1649">
              <div className="flex items-start gap-4 pr-4 w-full" data-id="element-1650">
                <div className={`p-3 rounded-2xl ${themeClasses} shadow-sm border shrink-0`} data-id="element-1651">
                  <IconComponent className="w-7 h-7" strokeWidth={2.5} data-id="element-1652" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-1653">
                  <h2 id="module-panel-title" className="text-2xl font-black text-slate-900 tracking-tight leading-tight" data-id="element-1654">
                    {title}
                  </h2>
                  <p className="text-slate-500 font-medium text-sm leading-snug mt-0.5 line-clamp-2 mb-2.5" data-id="element-1655">
                    {description}
                  </p>

                  {/* Inline Stats Badges */}
                  <div className="flex flex-wrap items-center gap-2" data-id="element-1656">
                    {/* Mastery Badge */}
                    <div className="flex items-center gap-1.5 bg-slate-100 px-2 py-1 rounded-md border border-slate-200" data-id="element-1657">
                      <div className="w-2.5 h-2.5 rounded-full" style={{
                    backgroundColor: masteryPercentage > 0 ? moduleHex : '#e2e8f0'
                  }} data-id="element-1658" />
                      <span className="text-xs font-bold text-slate-700" data-id="element-1659">
                        {masteryPercentage > 0 ? `${Math.round(masteryPercentage)}%` : '0%'}
                      </span>
                    </div>

                    {/* Niveau Badge */}
                    <div className="flex items-center gap-1.5 bg-slate-100 px-2 py-1 rounded-md border border-slate-200" data-id="element-1660">
                      <Target className="w-3 h-3 text-slate-500" data-id="element-1661" />
                      <span className="text-xs font-bold text-slate-700" data-id="element-1662">
                        {tierDisplay}
                      </span>
                    </div>

                    {/* Vragen Badge */}
                    <div className="flex items-center gap-1.5 bg-slate-100 px-2 py-1 rounded-md border border-slate-200" data-id="element-1663">
                      <CheckCircle className="w-3 h-3 text-slate-500" data-id="element-1664" />
                      <span className="text-xs font-bold text-slate-700" data-id="element-1665">
                        {answeredCount}{' '}
                        <span className="font-medium text-slate-500" data-id="element-1666">
                          vragen
                        </span>
                      </span>
                    </div>
                  </div>

                  {/* Small inline progress bar */}
                  <div className="mt-3 w-full bg-slate-100 rounded-full h-1.5 overflow-hidden" data-id="element-1667">
                    <div className="h-full rounded-full transition-all duration-500" style={{
                  width: `${masteryPercentage}%`,
                  backgroundColor: masteryPercentage > 0 ? moduleHex : '#e2e8f0'
                }} data-id="element-1668" />
                  </div>

                  {/* FG2 — Dual-progress signal: mastery fast-path / coverage gap.
                   Subtle pills that respect the fast learner without blocking. */}
                  {(tierUnlockedViaMastery || coverageGapHint) && <div className="mt-2.5 flex flex-wrap items-center gap-1.5" data-id="element-1669">
                      {tierUnlockedViaMastery && <motion.div initial={{
                  opacity: 0,
                  y: -4
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.25
                }} className="flex items-center gap-1.5 bg-emerald-50 text-emerald-700 px-2 py-1 rounded-md border border-emerald-100" data-id="element-1670">
                          <Sparkles className="w-3 h-3" data-id="element-1671" />
                          <span className="text-[11px] font-bold leading-none" data-id="element-1672">
                            Vrijgespeeld door mastery
                          </span>
                        </motion.div>}
                      {coverageGapHint && unseenVariantCount > 0 && <motion.div initial={{
                  opacity: 0,
                  y: -4
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.25,
                  delay: 0.05
                }} className="flex items-center gap-1.5 bg-amber-50 text-amber-700 px-2 py-1 rounded-md border border-amber-100" title="Je beheerst dit niveau — wil je ook de overige varianten nog eens zien?" data-id="element-1673">
                          <Eye className="w-3 h-3" data-id="element-1674" />
                          <span className="text-[11px] font-bold leading-none" data-id="element-1675">
                            {unseenVariantCount}{' '}
                            {unseenVariantCount === 1 ? 'variant' : 'varianten'}{' '}
                            nog niet gezien
                          </span>
                        </motion.div>}
                    </div>}
                </div>
              </div>
              <button onClick={onClose} className="p-2 rounded-full bg-slate-100 text-slate-400 hover:bg-slate-200 hover:text-slate-600 transition-colors shrink-0" aria-label="Paneel sluiten" data-id="element-1676">
                <X className="w-5 h-5" data-id="element-1677" />
              </button>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-6 flex flex-col scroll-smooth" data-id="element-1678">
              <div className="flex flex-col h-full space-y-6" data-id="element-1679">
                {/* 1. Smart Tip */}
                {strugglingSkill && <div className="bg-amber-50 border border-amber-200/60 rounded-2xl p-4 flex gap-3 shadow-sm" data-id="element-1680">
                    <div className="shrink-0 mt-0.5" data-id="element-1681">
                      <div className="bg-white p-2 rounded-xl shadow-sm text-amber-500 border border-amber-100" data-id="element-1682">
                        <Lightbulb className="w-5 h-5" data-id="element-1683" />
                      </div>
                    </div>
                    <div data-id="element-1684">
                      <h4 className="font-bold text-amber-900 text-sm mb-0.5" data-id="element-1685">
                        Focus Tip
                      </h4>
                      <p className="text-sm text-amber-800/80 font-medium leading-snug" data-id="element-1686">
                        <span className="font-bold capitalize text-amber-900" data-id="element-1687">
                          {formatSkill(strugglingSkill.skill)}
                        </span>{' '}
                        kan nog wat extra aandacht gebruiken.
                      </p>
                    </div>
                  </div>}

                {/* 2b. Review Tip (Decay) */}
                {decayingSkills.length > 0 && <div className="bg-teal-50 border border-teal-200/60 rounded-2xl p-4 flex gap-3 shadow-sm" data-id="element-1688">
                    <div className="shrink-0 mt-0.5" data-id="element-1689">
                      <div className="bg-white p-2 rounded-xl shadow-sm text-teal-500 border border-teal-100" data-id="element-1690">
                        <RefreshCw className="w-5 h-5" data-id="element-1691" />
                      </div>
                    </div>
                    <div data-id="element-1692">
                      <h4 className="font-bold text-teal-900 text-sm mb-0.5" data-id="element-1693">
                        Herhaling Nodig
                      </h4>
                      <p className="text-sm text-teal-800/80 font-medium leading-snug" data-id="element-1694">
                        Je hebt{' '}
                        <strong className="text-teal-900" data-id="element-1695">
                          {decayingSkills.length}{' '}
                          {decayingSkills.length === 1 ? 'vaardigheid' : 'vaardigheden'}
                        </strong>{' '}
                        al een tijdje niet geoefend.
                      </p>
                    </div>
                  </div>}

                {/* 3. Skill Mastery Section */}
                <div data-id="element-1696">
                  <div className="flex items-center gap-2 mb-4" data-id="element-1697">
                    <BarChart2 className="w-5 h-5 text-slate-400" data-id="element-1698" />
                    <h3 className="text-lg font-black text-slate-800" data-id="element-1699">
                      Jouw Vaardigheden
                    </h3>
                  </div>

                  {sortedSkills.length > 0 ? <div className="space-y-3" data-id="element-1700">
                      {sortedSkills.map(skill => {
                  const isMastered = skill.masteryScore >= 85;
                  const isGood = skill.masteryScore >= 50;
                  const isExpanded = expandedSkill === skill.skill;
                  const hasMultipleSubSkills = skill.subSkills.length > 1;
                  let colorClass = 'bg-amber-500';
                  let bgClass = 'bg-white border-slate-200/60';
                  let textClass = 'text-slate-700';
                  let scoreClass = 'text-amber-600 bg-amber-50';
                  if (isMastered) {
                    colorClass = 'bg-emerald-500';
                    textClass = 'text-slate-800';
                    scoreClass = 'text-emerald-700 bg-emerald-50';
                  } else if (isGood) {
                    colorClass = 'bg-orange-500';
                    textClass = 'text-slate-800';
                    scoreClass = 'text-orange-700 bg-orange-50';
                  }
                  return <div key={skill.skill} className={`rounded-xl border ${bgClass} shadow-sm overflow-hidden transition-all`} data-id="element-1701">
                            {/* Skill Row */}
                            <div className={`p-3.5 flex items-center justify-between gap-3 ${hasMultipleSubSkills ? 'cursor-pointer hover:bg-slate-50/50' : ''}`} onClick={() => {
                      if (hasMultipleSubSkills) {
                        setExpandedSkill(isExpanded ? null : skill.skill);
                      }
                    }} data-id="element-1702">
                              <div className="flex-1" data-id="element-1703">
                                <div className="flex items-center justify-between mb-2" data-id="element-1704">
                                  <div className="flex items-center gap-2" data-id="element-1705">
                                    {hasMultipleSubSkills && <div className="text-slate-400" data-id="element-1706">
                                        {isExpanded ? <ChevronDown className="w-4 h-4" data-id="element-1707" /> : <ChevronRight className="w-4 h-4" data-id="element-1708" />}
                                      </div>}
                                    <span className={`font-bold text-sm capitalize ${textClass}`} data-id="element-1709">
                                      {formatSkill(skill.skill)}
                                    </span>
                                    {skill.hasDecay && <div className="bg-teal-100 text-teal-700 p-1 rounded-full" title="Herhaling nodig" data-id="element-1710">
                                        <RefreshCw className="w-3 h-3" data-id="element-1711" />
                                      </div>}
                                  </div>

                                  <div className="flex items-center gap-2" data-id="element-1712">
                                    {skill.trend === 'improving' && <TrendingUp className="w-3.5 h-3.5 text-emerald-500" data-id="element-1713" />}
                                    {skill.trend === 'struggling' && <TrendingDown className="w-3.5 h-3.5 text-amber-500" data-id="element-1714" />}
                                    {skill.trend === 'stable' && <Minus className="w-3.5 h-3.5 text-slate-300" data-id="element-1715" />}
                                    <span className={`text-xs font-black px-1.5 py-0.5 rounded-md ${scoreClass}`} data-id="element-1716">
                                      {skill.masteryScore}%
                                    </span>
                                  </div>
                                </div>
                                <ProgressBar value={skill.masteryScore} height={4} color={colorClass} className="bg-slate-100" data-id="element-1717" />
                              </div>
                            </div>

                            {/* SubSkill Drill-Down */}
                            <AnimatePresence data-id="element-1718">
                              {isExpanded && hasMultipleSubSkills && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} transition={{
                        duration: 0.2
                      }} className="overflow-hidden bg-slate-50/80 border-t border-slate-100" data-id="element-1719">
                                  <div className="p-2 space-y-1.5" data-id="element-1720">
                                    {skill.subSkills.sort((a, b) => a.masteryScore - b.masteryScore).map(sub => {
                            const family = getFamilyBySubSkill(sub.subSkill);
                            const learningGoal = family ? getLearningGoal(family.familyId) : '';
                            return <div key={sub.subSkill} className="flex flex-col bg-white rounded-lg px-3 py-2 border border-slate-100 shadow-sm" data-id="element-1721">
                                            <div className="flex items-center justify-between" data-id="element-1722">
                                              <span className="text-xs font-medium text-slate-600 capitalize" data-id="element-1723">
                                                {formatSkill(sub.subSkill)}
                                              </span>
                                              <div className="flex items-center gap-2" data-id="element-1724">
                                                {sub.hasDecay && <RefreshCw className="w-3 h-3 text-teal-500" title="Herhaling nodig" data-id="element-1725" />}
                                                <span className="text-xs font-bold text-slate-500 bg-slate-50 px-1.5 py-0.5 rounded" data-id="element-1726">
                                                  {sub.masteryScore}%
                                                </span>
                                              </div>
                                            </div>
                                            {learningGoal && <div className="text-[11px] text-slate-400 font-normal mt-0.5 flex items-start gap-1" data-id="element-1727">
                                                <Target className="w-3 h-3 shrink-0 mt-[1px] text-slate-300" data-id="element-1728" />
                                                <span data-id="element-1729">{learningGoal}</span>
                                              </div>}
                                          </div>;
                          })}
                                  </div>
                                </motion.div>}
                            </AnimatePresence>
                          </div>;
                })}
                    </div> : <div className="text-center py-8 bg-white rounded-2xl border border-dashed border-slate-300" data-id="element-1730">
                      <div className="w-10 h-10 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-2" data-id="element-1731">
                        <Zap className="w-5 h-5 text-slate-400" data-id="element-1732" />
                      </div>
                      <p className="text-slate-500 font-medium text-sm" data-id="element-1733">
                        Start met oefenen om je vaardigheden te zien!
                      </p>
                    </div>}
                </div>

                {/* Bottom padding for sticky actions */}
                <div className="h-24 shrink-0" data-id="element-1734" />
              </div>
            </div>

            {/* Sticky Bottom Actions */}
            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[#FAFAF8] via-[#FAFAF8] to-transparent pt-16 pointer-events-none" data-id="element-1735">
              <div className="flex flex-col gap-3 pointer-events-auto" data-id="element-1736">
                <motion.button whileTap={{
              scale: 0.97
            }} onClick={() => onStartPractice(module.id)} className="w-full flex items-center justify-center font-bold rounded-2xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 hover:-translate-y-0.5 active:translate-y-[2px] active:shadow-none shadow-lg h-14 text-base group text-white" style={{
              backgroundColor: moduleHex,
              boxShadow: `0 4px 0 0 ${moduleHex}80, 0 10px 15px -3px ${moduleHex}40`
            }} data-id="element-1737">
                  <span className="font-black mr-2" data-id="element-1738">Start Oefensessie</span>
                  <div className="bg-white/20 rounded-md p-1 group-hover:bg-white/30 transition-colors" data-id="element-1739">
                    <Play className="w-4 h-4 fill-current" data-id="element-1740" />
                  </div>
                </motion.button>

                <Button onClick={() => onViewExplanation(module.id)} variant="ghost" size="lg" fullWidth className="text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 h-12" data-id="element-1741">
                  <Lightbulb className="w-4 h-4 mr-2" data-id="element-1742" />
                  <span className="font-bold" data-id="element-1743">Wat leer je hier?</span>
                </Button>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
});